import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/utils/api";
import {
  LayoutDashboard, Grid3x3, Database, Users, Truck, Package, Layers,
  Shield, BarChart2, Zap, FileCheck, AlertCircle, MapPin, LogOut,
  ChevronRight, Building2, Wrench, DollarSign, CalendarDays, TrendingUp, Box, Map,
  MessageSquare, ClipboardCheck, FileWarning, Activity, Clock, Calendar, Route, CheckSquare, ClipboardList, Target
} from "lucide-react";

// =============================================================================
// ADMIN - Full system access
// =============================================================================
const ADMIN_NAV = [
  { section: "COMMAND" },
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/command-center", icon: Grid3x3, label: "Command Center" },
  { to: "/work-activity", icon: Activity, label: "Work Activity" },
  { to: "/circle-heatmap", icon: BarChart2, label: "Circle Heatmap" },
  { to: "/field-map", icon: Map, label: "Field Map" },
  { section: "OPERATIONS" },
  { to: "/dt-master", icon: Database, label: "DT Master" },
  { to: "/hierarchy", icon: Layers, label: "Hierarchy" },
  { to: "/shutdowns", icon: Zap, label: "Shutdowns" },
  { to: "/holds", icon: AlertCircle, label: "Hold Tracking" },
  { to: "/qc", icon: FileCheck, label: "QC / Rework" },
  { section: "FIELD FORCE" },
  { to: "/daily-roster", icon: CalendarDays, label: "Daily Rosters" },
  { to: "/assignments", icon: ClipboardList, label: "Feeder Assignments" },
  { to: "/employees", icon: Users, label: "Employees" },
  { section: "ATTENDANCE & TA" },
  { to: "/attendance", icon: Clock, label: "Attendance" },
  { to: "/leave", icon: Calendar, label: "Leave Mgmt" },
  { to: "/travel", icon: Route, label: "Travel Allowance" },
  { section: "AUDIT & COMPLIANCE" },
  { to: "/audit", icon: ClipboardCheck, label: "Audits" },
  { section: "ASSETS" },
  { to: "/locations", icon: MapPin, label: "Location Master" },
  { to: "/inventory", icon: Package, label: "Inventory" },
  { to: "/vehicles", icon: Truck, label: "Vehicles" },
  { section: "COMMUNICATION" },
  { to: "/communications", icon: MessageSquare, label: "Comm Log" },
  { section: "FINANCE" },
  { to: "/finance", icon: DollarSign, label: "Finance" },
];

// =============================================================================
// PACKAGE HEAD - Package-level view (multiple circles)
// NO access to hierarchy management, only viewing
// =============================================================================
const PACKAGE_HEAD_NAV = [
  { section: "PACKAGE OVERVIEW" },
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/command-center", icon: Grid3x3, label: "Command Center" },
  { to: "/work-activity", icon: Activity, label: "Work Activity" },
  { to: "/circle-heatmap", icon: BarChart2, label: "Circle Heatmap" },
  { to: "/field-map", icon: Map, label: "Field Map" },
  { section: "OPERATIONS" },
  { to: "/dt-master", icon: Database, label: "DT Master" },
  { to: "/hierarchy", icon: Layers, label: "Feeder Master" },
  { to: "/shutdowns", icon: Zap, label: "Shutdowns" },
  { to: "/holds", icon: AlertCircle, label: "Hold Tracking" },
  { to: "/qc", icon: FileCheck, label: "QC / Rework" },
  { section: "FIELD FORCE" },
  { to: "/daily-roster", icon: CalendarDays, label: "Daily Rosters" },
  { to: "/employees", icon: Users, label: "Employees" },
  { section: "ATTENDANCE & TA" },
  { to: "/attendance", icon: Clock, label: "Attendance" },
  { to: "/leave", icon: Calendar, label: "Leave Mgmt" },
  { to: "/travel", icon: Route, label: "Travel Allowance" },
  { section: "COMMUNICATION" },
  { to: "/communications", icon: MessageSquare, label: "Comm Log" },
];

// =============================================================================
// CIRCLE INCHARGE - Circle-level view
// =============================================================================
const CI_NAV = [
  { section: "CIRCLE" },
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/command-center", icon: Grid3x3, label: "Command Center" },
  { to: "/work-activity", icon: Activity, label: "Work Activity" },
  { to: "/field-map", icon: Map, label: "Field Map" },
  { section: "OPERATIONS" },
  { to: "/dt-master", icon: Database, label: "DT Master" },
  { to: "/hierarchy", icon: Layers, label: "Feeder Master" },
  { to: "/feeder-assignments", icon: ClipboardList, label: "Survey Assignments" },
  { to: "/shutdowns", icon: Zap, label: "Shutdowns" },
  { to: "/holds", icon: AlertCircle, label: "Hold Tracking" },
  { to: "/qc", icon: FileCheck, label: "QC / Rework" },
  { section: "FIELD FORCE" },
  { to: "/daily-roster", icon: CalendarDays, label: "Daily Rosters" },
  { to: "/employees", icon: Users, label: "Employees" },
  { to: "/vehicles", icon: Truck, label: "Driver Assignments" },
  { section: "ATTENDANCE & TA" },
  { to: "/attendance", icon: Clock, label: "Attendance" },
  { to: "/leave", icon: Calendar, label: "Leave Approvals" },
  { to: "/travel", icon: Route, label: "TA Review" },
  { section: "AUDIT & COMPLIANCE" },
  { to: "/audit/my-caps", icon: FileWarning, label: "My CAPs" },
  { section: "COMMUNICATION" },
  { to: "/communications", icon: MessageSquare, label: "Comm Log" },
  { section: "FINANCE" },
  { to: "/finance", icon: DollarSign, label: "Finance" },
];

// =============================================================================
// DIVISION INCHARGE - Division-level view (subset of circle)
// =============================================================================
const DI_NAV = [
  { section: "DIVISION" },
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/work-activity", icon: Activity, label: "Work Activity" },
  { to: "/field-map", icon: Map, label: "Field Map" },
  { section: "OPERATIONS" },
  { to: "/dt-master", icon: Database, label: "DT Master" },
  { to: "/shutdowns", icon: Zap, label: "Shutdowns" },
  { to: "/holds", icon: AlertCircle, label: "Hold Tracking" },
  { section: "FIELD FORCE" },
  { to: "/daily-roster", icon: CalendarDays, label: "Daily Rosters" },
  { to: "/employees", icon: Users, label: "Employees" },
  { section: "ATTENDANCE & TA" },
  { to: "/attendance", icon: Clock, label: "Attendance" },
  { to: "/leave", icon: Calendar, label: "Leave Approvals" },
  { to: "/travel", icon: Route, label: "TA Review" },
  { section: "COMMUNICATION" },
  { to: "/communications", icon: MessageSquare, label: "Comm Log" },
];

// =============================================================================
// SUPERVISOR - Feeder-level view (assigned feeders only)
// =============================================================================
const SUPERVISOR_NAV = [
  { section: "FIELD OPS" },
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/field-ops", icon: Target, label: "Field Ops Command" },
  { to: "/work-activity", icon: Activity, label: "Team Activity" },
  { to: "/field-map", icon: Map, label: "Field Map" },
  { section: "MY FEEDERS" },
  { to: "/dt-master", icon: Database, label: "DT Master" },
  { to: "/shutdowns", icon: Zap, label: "Shutdowns" },
  { to: "/holds", icon: AlertCircle, label: "Hold Tracking" },
  { section: "ATTENDANCE & TA" },
  { to: "/attendance", icon: Clock, label: "Attendance" },
  { to: "/leave", icon: Calendar, label: "Leave Approvals" },
  { to: "/travel", icon: Route, label: "TA Review" },
  { section: "ASSETS" },
  { to: "/inventory", icon: Package, label: "Issue Inventory" },
  { to: "/vehicles", icon: Truck, label: "Driver Assignments" },
  { section: "COMMUNICATION" },
  { to: "/communications", icon: MessageSquare, label: "Comm Log" },
  { to: "/my-performance", icon: TrendingUp, label: "My Performance" },
];

// =============================================================================
// TECHNICIAN - SIMPLIFIED 4-TAB MOBILE VIEW
// Redirect to /tech-home for mobile-optimized experience
// =============================================================================
const TECHNICIAN_NAV = [
  { to: "/tech-home", icon: LayoutDashboard, label: "📱 Mobile View", highlight: true },
  { section: "LEGACY PAGES" },
  { to: "/dt-master", icon: Database, label: "DT Master" },
  { to: "/my-inventory", icon: Package, label: "My Inventory" },
  { to: "/attendance", icon: Clock, label: "Attendance" },
  { to: "/my-performance", icon: TrendingUp, label: "Performance" },
];

// =============================================================================
// HELPER - Attendance focused, DT access via roster feeders
// =============================================================================
const HELPER_NAV = [
  { section: "MY WORK" },
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/daily-roster", icon: CalendarDays, label: "My Task Force" },
  { to: "/dt-master", icon: Database, label: "My DTs" },
  { section: "ATTENDANCE" },
  { to: "/attendance", icon: Clock, label: "Punch In/Out" },
  { to: "/leave", icon: Calendar, label: "Leave" },
  { to: "/travel", icon: Route, label: "My Travel" },
  { to: "/my-performance", icon: TrendingUp, label: "My Attendance" },
];

// =============================================================================
// SURVEYOR - Survey-focused with feeder assignments
// =============================================================================
const SURVEYOR_NAV = [
  { section: "SURVEYS" },
  { to: "/feeder-assignments", icon: Database, label: "My Feeders" },
  { to: "/dt-master", icon: Database, label: "Survey DTs" },
  { to: "/field-map", icon: Map, label: "Field Map" },
  { section: "ATTENDANCE" },
  { to: "/attendance", icon: Clock, label: "Punch In/Out" },
  { to: "/leave", icon: Calendar, label: "Leave" },
  { to: "/travel", icon: Route, label: "My Travel" },
  { section: "PERFORMANCE" },
  { to: "/my-performance", icon: TrendingUp, label: "My Performance" },
];

// =============================================================================
// DRIVER - Vehicle & attendance only, ZERO DT access
// =============================================================================
const DRIVER_NAV = [
  { section: "VEHICLE" },
  { to: "/vehicles", icon: Truck, label: "My Vehicle" },
  { section: "ATTENDANCE" },
  { to: "/attendance", icon: Clock, label: "Punch In/Out" },
  { to: "/leave", icon: Calendar, label: "Leave" },
  { to: "/travel", icon: Route, label: "My Travel" },
];

// =============================================================================
// VEHICLE INCHARGE - Fleet management ONLY, ZERO DT access
// =============================================================================
const VEHICLE_INCHARGE_NAV = [
  { section: "FLEET MANAGEMENT" },
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/vehicles", icon: Truck, label: "Vehicle Management" },
  { section: "MY DRIVERS" },
  { to: "/employees", icon: Users, label: "Drivers" },
  { section: "ATTENDANCE" },
  { to: "/attendance", icon: Clock, label: "Attendance" },
  { to: "/leave", icon: Calendar, label: "Leave" },
  { to: "/travel", icon: Route, label: "TA Module" },
];

// =============================================================================
// WAREHOUSE MANAGER - Inventory ONLY, ZERO DT access
// =============================================================================
const WAREHOUSE_MANAGER_NAV = [
  { section: "WAREHOUSE" },
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/inventory", icon: Package, label: "Inventory" },
  { to: "/locations", icon: MapPin, label: "Locations" },
  { section: "STAFF" },
  { to: "/employees", icon: Users, label: "Warehouse Staff" },
  { section: "ATTENDANCE" },
  { to: "/attendance", icon: Clock, label: "Attendance" },
  { to: "/leave", icon: Calendar, label: "Leave" },
  { section: "COMMUNICATION" },
  { to: "/communications", icon: MessageSquare, label: "Comm Log" },
];

// =============================================================================
// FINANCE - Billing/Invoice focused, global view for billing
// =============================================================================
const FINANCE_NAV = [
  { section: "FINANCE" },
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/finance", icon: DollarSign, label: "Finance & Billing" },
  { section: "REPORTS" },
  { to: "/work-activity", icon: Activity, label: "Work Activity" },
  { to: "/dt-master", icon: Database, label: "DT Master" },
  { section: "ATTENDANCE & TA" },
  { to: "/attendance", icon: Clock, label: "Attendance" },
  { to: "/leave", icon: Calendar, label: "Leave Summary" },
  { to: "/travel", icon: Route, label: "TA Summary" },
  { section: "COMMUNICATION" },
  { to: "/communications", icon: MessageSquare, label: "Comm Log" },
];

// =============================================================================
// AUDITOR - Audit focused (all auditor types share same nav)
// =============================================================================
const AUDITOR_NAV = [
  { section: "AUDITS" },
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/audit", icon: ClipboardCheck, label: "Audit Schedules" },
  { to: "/audit/my-caps", icon: CheckSquare, label: "CAP Resolution" },
  { section: "REFERENCE" },
  { to: "/dt-master", icon: Database, label: "DT Master" },
  { to: "/inventory", icon: Package, label: "Inventory" },
  { section: "ATTENDANCE" },
  { to: "/attendance", icon: Clock, label: "Attendance" },
  { to: "/leave", icon: Calendar, label: "Leave" },
  { to: "/travel", icon: Route, label: "My Travel" },
];

// =============================================================================
// TECHNICIAN WITH SURVEY CAPABILITY (dual role)
// =============================================================================
const TECHNICIAN_SURVEYOR_NAV = [
  { section: "MY WORK" },
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/daily-roster", icon: CalendarDays, label: "My Task Force" },
  { to: "/field-map", icon: Map, label: "Field Map" },
  { section: "SURVEYS" },
  { to: "/feeder-assignments", icon: Database, label: "Survey Feeders" },
  { section: "INSTALLATIONS" },
  { to: "/dt-master", icon: Database, label: "My Assignments" },
  { section: "ATTENDANCE" },
  { to: "/attendance", icon: Clock, label: "Punch In/Out" },
  { to: "/leave", icon: Calendar, label: "Leave" },
  { to: "/travel", icon: Route, label: "My Travel" },
  { section: "INVENTORY" },
  { to: "/my-inventory", icon: Box, label: "My Inventory" },
  { section: "PERFORMANCE" },
  { to: "/my-performance", icon: TrendingUp, label: "My Performance" },
];

// =============================================================================
// DYNAMIC DRIVER SECTION - Added to non-driver roles when assigned as driver
// =============================================================================
const DRIVER_SECTION = [
  { section: "DRIVER SECTION" },
  { to: "/vehicles", icon: Truck, label: "My Vehicle" },
];

// =============================================================================
// ROLE TO NAV MAPPING
// =============================================================================
const NAV_BY_ROLE = {
  admin: ADMIN_NAV,
  package_head: PACKAGE_HEAD_NAV,  // FIXED: Was ADMIN_NAV
  ci: CI_NAV,
  di: DI_NAV,  // FIXED: Was CI_NAV
  supervisor: SUPERVISOR_NAV,
  technician: TECHNICIAN_NAV,
  helper: HELPER_NAV,
  driver: DRIVER_NAV,
  vehicle_incharge: VEHICLE_INCHARGE_NAV,
  surveyor: SURVEYOR_NAV,
  technician_surveyor: TECHNICIAN_SURVEYOR_NAV,
  warehouse_manager: WAREHOUSE_MANAGER_NAV,  // FIXED: Was ADMIN_NAV
  warehouse_incharge: WAREHOUSE_MANAGER_NAV,
  finance: FINANCE_NAV,  // FIXED: Was ADMIN_NAV
  // Auditor roles
  auditor: AUDITOR_NAV,
  inventory_auditor: AUDITOR_NAV,
  safety_auditor: AUDITOR_NAV,
  process_auditor: AUDITOR_NAV,
};

export default function Sidebar({ onClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isAssignedDriver, setIsAssignedDriver] = useState(false);
  
  // Check if user is assigned as a driver (for non-driver roles)
  useEffect(() => {
    if (user?.role && user.role !== "driver") {
      api.get("/vehicles/my-vehicle-assignments")
        .then(res => setIsAssignedDriver(res.data.is_driver === true))
        .catch(() => setIsAssignedDriver(false));
    }
  }, [user?.role]);
  
  // Compute navItems based on role and driver assignment
  const navItems = React.useMemo(() => {
    // Check for dual role capability
    let navRole = user?.role;
    if (user?.role === "technician" && user?.can_survey) {
      navRole = "technician_surveyor";
    }
    let items = NAV_BY_ROLE[navRole] || TECHNICIAN_NAV;  // Default to restricted nav
    
    // Add Driver Section if user is assigned as driver but isn't a driver role
    if (isAssignedDriver && user?.role !== "driver") {
      items = [...items, ...DRIVER_SECTION];
    }
    
    return items;
  }, [user?.role, user?.can_survey, isAssignedDriver]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="flex flex-col h-full sidebar" data-testid="sidebar">
      {/* Logo */}
      <div className="px-4 py-5 border-b border-slate-800">
        <div className="text-orange-500 font-black text-lg tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
          INOVIT
        </div>
        <div className="text-slate-500 text-[10px] tracking-widest uppercase">Smart Meter ERP</div>
      </div>

      {/* User Info */}
      <div className="px-4 py-3 border-b border-slate-800 flex items-center gap-3">
        <div className="w-8 h-8 rounded-sm bg-orange-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
          {user?.full_name?.charAt(0) || "U"}
        </div>
        <div className="min-w-0">
          <div className="text-white text-xs font-semibold truncate">{user?.full_name?.split(" ")[0] || "User"}</div>
          <div className="text-slate-400 text-[10px] uppercase tracking-wide">{user?.role}</div>
          {user?.circle_name && <div className="text-orange-400 text-[10px]">{user.circle_name}</div>}
          {user?.package_name && !user?.circle_name && <div className="text-blue-400 text-[10px]">{user.package_name} (Package)</div>}
        </div>
      </div>

      {/* Nav Items */}
      <nav className="flex-1 overflow-y-auto py-2 scrollbar-hide">
        {navItems.map((item, i) => {
          if (item.section) {
            return <div key={i} className="sidebar-section">{item.section}</div>;
          }
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onClose}
              className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <Icon size={16} strokeWidth={1.5} />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="p-4 border-t border-slate-800">
        <button
          onClick={handleLogout}
          className="w-full sidebar-item text-red-400 hover:text-red-300 hover:bg-red-900/20"
          data-testid="sidebar-logout"
        >
          <LogOut size={16} />
          <span>Sign Out</span>
        </button>
      </div>
    </div>
  );
}
